import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Loader2, FileUp, Save } from 'lucide-react';
import type { ImportResult, ImportError, ImportAction } from '@mdu-invoicing/shared';
import { useAuth } from '@/hooks/useAuth';
import { apiFetch, ApiError } from '@/lib/api-client';
import { monthBounds } from '@/lib/eligibles-query';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

type Stream = 'SSV' | 'TSA';

// Mirrors the API ImportPreview (api is not importable from the frontend bundle).
interface ImportPreview {
  fileKind: 'xlsx' | 'csv';
  ssv: ImportResult | null;
  tsa: ImportResult | null;
}

function errorText(e: ImportError): string {
  switch (e.code) {
    case 'DUPLICATE_MDU_ID':
      return `MDU_ID « ${e.mduId} » en double (lignes ${e.rows.join(', ')})`;
    case 'MISSING_MDU_ID':
      return `MDU_ID manquant (ligne ${e.row})`;
    case 'INVALID_ELIGIBILITY':
      return `Éligibilité invalide « ${e.value} » (ligne ${e.row}, MDU ${e.mduId}) — attendu OK / REMOVE / ADD`;
    case 'MISSING_COMMENT':
      return `Commentaire obligatoire pour ${e.eligibility} (ligne ${e.row}, MDU ${e.mduId})`;
  }
}

/** What a successful commit wrote — lets Éligibles show exactly what was just imported. */
export interface ImportCommitInfo {
  importBatchId: string | null;
  importFilename: string | null;
  persisted: number;
}

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * The invoicing month the grid is showing ('YYYY-MM'). MUST be forwarded to the API:
   * both /import and /import/commit derive their period from ?from&to and fall back to
   * the CURRENT month otherwise — so omitting it matched the file against the wrong
   * month (nothing persisted, nothing visible). Gerd 2026-08.
   */
  month: string;
  /** Called after a successful commit so the Éligibles grid refreshes in place. */
  onCommitted: (info: ImportCommitInfo) => void;
}

/**
 * Import & provider-validate as a modal launched from Éligibles. Upload → analyse →
 * preview → commit; on success the grid refreshes in place (no navigation). Both roles
 * may commit (staff = TEMPORARY verification layer) and the server re-validates the file
 * (client actions are not trusted).
 */
export function ImportModal({ open, onClose, month, onCommitted }: ImportModalProps) {
  const { isProvider, user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [stream, setStream] = useState<Stream>('SSV');
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset when the modal closes so a re-open starts clean.
  useEffect(() => {
    if (!open) {
      setFile(null);
      setPreview(null);
      setBusy(false);
      setCommitting(false);
    }
  }, [open]);

  // The period every call targets = the month the grid is showing (never the server's
  // "current month" fallback). Both the preview (which matches MDU_IDs against that
  // month's eligibles) and the commit (which writes `invoicingMonth`) need it.
  const { from, to } = monthBounds(month);
  const periodQs = `from=${from}&to=${to}`;
  const monthLabel = new Date(`${month}-01T00:00:00`).toLocaleDateString('fr-BE', {
    month: 'long',
    year: 'numeric',
  });

  const isCsv = file ? file.name.toLowerCase().endsWith('.csv') : false;
  const rejected = preview ? preview.ssv?.status === 'rejected' || preview.tsa?.status === 'rejected' : true;
  const hasStream = !!(preview?.ssv || preview?.tsa);
  // Both roles may commit (staff = TEMPORARY verification layer); the server enforces
  // attribution + the provider-needs-contractor rule.
  const canCommit = hasStream && !rejected;

  async function handleAnalyze() {
    if (!file) return;
    setBusy(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (isCsv) fd.append('stream', stream);
      const res = await apiFetch<ImportPreview>(`/import?${periodQs}`, { method: 'POST', body: fd });
      setPreview(res);
      const isRejected = res.ssv?.status === 'rejected' || res.tsa?.status === 'rejected';
      if (isRejected) toast.error('Fichier rejeté — corrigez les erreurs puis ré-importez');
      else toast.success('Aperçu généré');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Échec de l'analyse du fichier");
    } finally {
      setBusy(false);
    }
  }

  // Re-send the SAME file to commit (the server re-validates — it never trusts
  // client-sent actions) and persist the decisions into the working set.
  async function handleCommit() {
    if (!file || !canCommit) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (isCsv) fd.append('stream', stream);
      const res = await apiFetch<
        ImportPreview & {
          committed: boolean;
          persisted: number;
          skipped: number;
          importBatchId: string | null;
          importFilename: string | null;
        }
      >(`/import/commit?${periodQs}`, { method: 'POST', body: fd });
      if (res.committed) {
        const extra = res.skipped > 0 ? `, ${res.skipped} ligne(s) ignorée(s)` : '';
        // A commit that wrote NOTHING used to show a green "0 décision(s) enregistrée(s)",
        // which reads as a success — say plainly that nothing was applied and why.
        if (res.persisted === 0) {
          toast.warning(
            `Aucune décision enregistrée pour ${monthLabel} — aucune ligne du fichier ne correspond aux éligibles de ce mois. Vérifiez le mois sélectionné.`,
          );
        } else {
          toast.success(`Import validé — ${res.persisted} décision(s) enregistrée(s)${extra}`);
        }
        // Refresh the grid in place AND show what was just imported.
        onCommitted({
          importBatchId: res.importBatchId,
          importFilename: res.importFilename,
          persisted: res.persisted,
        });
        onClose();
      } else {
        toast.error('Fichier rejeté à la validation — corrigez puis ré-importez');
        setPreview(res);
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Échec de la validation de l'import");
    } finally {
      setCommitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Import &amp; validation" className="max-w-3xl">
      <div className="space-y-5">
        <p className="text-sm text-slate-500">
          Charge le fichier <span className="font-mono text-xs">IMPORT_FOR_PARTNER_VALIDATION_…</span> rempli par le
          partenaire (OK / REMOVE / ADD). L'aperçu montre ce qui serait appliqué ; clique sur{' '}
          <span className="font-semibold">Valider l'import</span> pour enregistrer.
        </p>

        {/* The import applies to the month the grid is showing — state it, since a file
            exported for another month simply won't match (Gerd 2026-08). */}
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Mois de facturation ciblé : <span className="font-semibold text-slate-800">{monthLabel}</span> — les lignes du
          fichier sont rapprochées des éligibles de ce mois.
        </p>

        {/* The validation rules are all-or-nothing (OQ-6 mandatory comment, OQ-4 duplicate
            MDU_ID). State them BEFORE the upload so a rejection is never a surprise —
            the partner fills the file offline and only finds out here (Jeremy 2026-08-12). */}
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
          <p className="flex items-center gap-1.5 font-semibold">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            Règles de validation — le fichier est accepté ou rejeté en entier
          </p>
          <ul className="mt-1.5 list-disc space-y-1 pl-5">
            <li>
              Toute ligne <span className="font-semibold">REMOVE</span> ou <span className="font-semibold">ADD</span>{' '}
              doit porter un commentaire (colonne <span className="font-mono">Eligibility comment</span>).
            </li>
            <li>
              Un même <span className="font-mono">MDU_ID</span> ne peut apparaître qu'une seule fois par flux.
            </li>
            <li>
              À la moindre erreur, <span className="font-semibold">aucune ligne n'est enregistrée</span> — y compris
              celles de l'autre flux (dans un .xlsx, SSV et TSA sont validés ensemble).
            </li>
          </ul>
        </div>

        {/* Upload */}
        <div className="rounded-xl border border-slate-200 p-4">
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.csv"
            className="hidden"
            aria-label="Choisir un fichier"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setPreview(null);
            }}
          />
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                // Reset the value so re-picking the SAME (just-corrected) file still fires onChange.
                if (inputRef.current) inputRef.current.value = '';
                inputRef.current?.click();
              }}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <FileUp className="h-4 w-4 text-brand-purple" />
              Choisir un fichier (.xlsx / .csv)
            </button>
            {file ? (
              <span className="flex items-center gap-1.5 text-sm text-slate-600">
                <FileSpreadsheet className="h-4 w-4 text-slate-400" />
                <span className="font-medium text-slate-800">{file.name}</span>
              </span>
            ) : (
              <span className="text-sm text-slate-400">Aucun fichier sélectionné</span>
            )}
          </div>

          {/* Stream selector — only meaningful for a single-sheet .csv */}
          {isCsv && (
            <div className="mt-4">
              <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
                Flux du fichier .csv
              </div>
              <div
                role="radiogroup"
                aria-label="Flux du fichier .csv"
                className="inline-flex rounded-xl border border-slate-200 bg-white p-1"
              >
                {(['SSV', 'TSA'] as Stream[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="radio"
                    aria-checked={stream === s}
                    onClick={() => setStream(s)}
                    className={cn(
                      'rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors',
                      stream === s ? 'bg-brand-gradient text-white shadow-sm' : 'text-slate-500 hover:text-slate-800',
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-slate-400">
                Un .csv ne porte qu'un seul flux ; un .xlsx contient les deux onglets (SSV + TSA).
              </p>
            </div>
          )}

          <div className="mt-5">
            <button
              type="button"
              onClick={handleAnalyze}
              disabled={!file || busy}
              aria-busy={busy}
              className="flex items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {busy ? 'Analyse…' : 'Analyser le fichier'}
            </button>
          </div>
        </div>

        {/* Preview */}
        {preview && (
          <div className="space-y-4" aria-live="polite">
            {preview.ssv && <StreamResult stream="SSV" result={preview.ssv} />}
            {preview.tsa && <StreamResult stream="TSA" result={preview.tsa} />}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
              <p className="text-xs text-slate-400">
                Périmètre :{' '}
                <span className="font-semibold text-brand-purple">
                  {isProvider ? (user?.contractorName ?? '—') : 'Tous les contractants'}
                </span>
                {!isProvider && ' — vérification Unifiber : les lignes ADD (nouveaux MDU) ne sont pas enregistrées.'}
              </p>
              {canCommit && (
                <button
                  type="button"
                  onClick={handleCommit}
                  disabled={committing}
                  aria-busy={committing}
                  className="flex items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {committing ? 'Validation…' : 'Valider l’import (enregistrer)'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function StreamResult({ stream, result }: { stream: Stream; result: ImportResult }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <span className="text-sm font-bold text-slate-900">Flux {stream}</span>
        {result.status === 'rejected' ? (
          <Badge variant="red" dot>
            Rejeté
          </Badge>
        ) : (
          <Badge variant="green" dot>
            Aperçu
          </Badge>
        )}
      </div>

      {result.status === 'rejected' ? (
        <div className="space-y-2 px-5 py-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-rose-700">
            <AlertTriangle className="h-4 w-4" />
            {result.errors.length} erreur(s) — le fichier entier est rejeté
          </div>
          <ul className="space-y-1">
            {result.errors.map((e, i) => (
              <li key={i} className="rounded-lg bg-rose-50 px-3 py-1.5 text-sm text-rose-700">
                {errorText(e)}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap gap-2 px-5 py-4">
            <Stat label="OK" value={result.summary.byDecision.OK} tone="green" />
            <Stat label="REMOVE" value={result.summary.byDecision.REMOVE} tone="rose" />
            <Stat label="ADD" value={result.summary.byDecision.ADD} tone="amber" />
            <Stat label="Lignes lues" value={result.summary.rows} tone="slate" />
          </div>
          {result.actions.length > 0 && (
            <div className="overflow-x-auto border-t border-slate-100 custom-scrollbar">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-5 py-2.5">MDU_ID</th>
                    <th className="px-5 py-2.5">Action</th>
                    <th className="px-5 py-2.5">Décision</th>
                    <th className="px-5 py-2.5">Commentaire</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {result.actions.map((a: ImportAction) => (
                    <tr key={a.mduId} className="hover:bg-slate-50/60">
                      <td className="whitespace-nowrap px-5 py-2 font-mono text-xs text-slate-700">{a.mduId}</td>
                      <td className="px-5 py-2">
                        {a.type === 'add' ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Badge variant="amber">Ajout</Badge>
                            <Badge variant="red" dot>
                              DMP
                            </Badge>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-slate-600">
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                            Mise à jour
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-2 font-semibold text-slate-700">{a.eligibility}</td>
                      <td className="px-5 py-2 text-slate-500">{a.comment || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'green' | 'amber' | 'slate' | 'rose';
}) {
  const tones = {
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    slate: 'border-slate-200 bg-slate-50 text-slate-600',
    rose: 'border-rose-200 bg-rose-50 text-rose-700',
  };
  return (
    <span className={cn('rounded-lg border px-3 py-1.5 text-sm font-semibold', tones[tone])}>
      {value} <span className="font-medium opacity-80">{label}</span>
    </span>
  );
}
